// Fingerprint (device biometrics) before a door is unlocked — phone only.
//
// Decided 2026-09-29: on the phone every door-lock action (Unlock on 3 and 102,
// and creating a guest password on 102) is gated by the phone's own biometric
// prompt. Where there is NO usable biometric — no sensor, nothing enrolled, the
// web, the desktop — the old 4-digit app PIN (the account password) is the
// fallback. Wall Mode lock buttons are deliberately NOT gated.
//
// Plugin: @aparajita/capacitor-biometric-auth v8 (the Capacitor 6 line). Native,
// so it ships in the APK — an OTA to a phone without it finds the plugin missing
// and falls back to the PIN, which is the safe direction.
//
// ⚠️ THE PHONE'S OWN SCREEN LOCK COUNTS TOO (owner, 2026-10-13: "some users
// are not using fingerprint, add gesture also"). allowDeviceCredential is TRUE,
// so the system prompt accepts the fingerprint OR the phone's pattern / PIN /
// password, and a phone with a screen lock but no fingerprint goes straight to
// that lock screen ('device'). The app's 4-digit PIN is now only for a phone
// with NO screen lock at all. Until that date it was FALSE on purpose — the app
// PIN was the only fallback. The plugin already in the APK supports this, so it
// ships by OTA.
//
// ⚠️ This is a gate on a convenience action in the app, not the lock's security —
// the lock itself authorises the command (see docs/15).

import { isNativePlatform } from '../platform';

// ⚠️ NEVER RESOLVE A PROMISE WITH A CAPACITOR PLUGIN. The plugin is a Proxy that
// answers EVERY property with a native-method wrapper — `then` included
// (@capacitor/core only special-cases $$typeof and toJSON). So a promise resolved
// with it treats it as a thenable, calls a native "then" that never answers, and
// the await hangs forever. That was the door screen's endless spinner on a phone
// with a fingerprint enrolled (2026-09-29); jest's mock hid it. Wrapped in a
// plain object, it is just a value.
let pluginPromise = null;
const plugin = () => {
  if (!pluginPromise) {
    pluginPromise = import('@aparajita/capacitor-biometric-auth')
      .then((m) => ({ api: m.BiometricAuth }))
      .catch(() => ({ api: null }));
  }
  // The WRAPPER — callers destructure it (`const { api } = await plugin()`), so no
  // promise ever resolves with the Proxy itself.
  return pluginPromise;
};

// ⚠️ THE CHECK MUST NOT HANG THE DOOR SCREEN. Seen on a test phone 2026-09-29:
// checkBiometry never resolved, the screen sat on a spinner with no keypad, and
// every open fell through to the PIN dialog. So the answer is raced against a
// short timeout and a silent plugin means 'pin' — the owner's chosen fallback.
export const BIOMETRY_CHECK_TIMEOUT_MS = 2000;

const withTimeout = (promise, ms, fallback) => Promise.race([
  promise,
  new Promise((resolve) => { setTimeout(() => resolve(fallback), ms); }),
]);

/**
 * -> 'biometric'  a fingerprint/face is enrolled (the prompt also offers the screen lock)
 *    'device'     no biometric, but the phone has a pattern / PIN / password screen lock
 *    'pin'        neither — the app's 4-digit PIN
 * Never throws, never takes longer than BIOMETRY_CHECK_TIMEOUT_MS.
 */
export function unlockMethod() {
  return withTimeout(checkMethod(), BIOMETRY_CHECK_TIMEOUT_MS, 'pin');
}

async function checkMethod() {
  if (!isNativePlatform()) return 'pin';
  try {
    const { api: BiometricAuth } = await plugin();
    if (!BiometricAuth) return 'pin';
    const info = await BiometricAuth.checkBiometry();
    if (info && info.isAvailable) return 'biometric';
    return info && info.deviceIsSecure ? 'device' : 'pin';
  } catch (e) {
    return 'pin';
  }
}

/** Does this method use the phone's own prompt (fingerprint or screen lock)? */
export const usesPhonePrompt = (method) => method === 'biometric' || method === 'device';

/**
 * Show the phone's prompt — fingerprint, or the phone's pattern / PIN / password.
 * -> { ok: true } | { ok: false, cancelled: boolean, message: string }
 */
export async function verifyBiometric(reason = 'Confirm it is you') {
  try {
    const { api: BiometricAuth } = await plugin();
    if (!BiometricAuth) return { ok: false, cancelled: false, message: 'The phone check is not available on this phone.' };
    // No cancelTitle: Android allows no Cancel button beside "Use pattern"
    // (the plugin drops it); back / tapping outside cancels.
    await BiometricAuth.authenticate({
      reason,
      allowDeviceCredential: true,
      androidTitle: 'Smart Life India',
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return { ok: true };
  } catch (e) {
    const code = String((e && e.code) || '');
    const cancelled = code === 'userCancel' || code === 'appCancel' || code === 'systemCancel';
    let message = 'Not recognised. Try again.';
    if (cancelled) message = '';
    else if (code === 'biometryLockout') message = 'Too many attempts. Wait a moment, then try again.';
    else if (code === 'noDeviceCredential') message = 'No screen lock is set on this phone.';
    else if (code === 'biometryNotEnrolled' || code === 'biometryNotAvailable') message = 'No fingerprint or screen lock is set up on this phone.';
    return { ok: false, cancelled, message };
  }
}

/** The 4-digit fallback: the account's own password, as the door screen always used. */
export const pinMatches = (entered, user) =>
  String(entered || '') !== '' && String(entered) === String((user && user.Password) ?? '');
